import { createFileRoute } from "@tanstack/react-router";
import { withMemberGate } from "@/lib/with-member-gate";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { getMoodChecks, createMoodCheck } from "@/lib/mood.functions";
import { TrendingUp, Sparkles } from "lucide-react";
import { reportCrisisIfDetected } from "@/lib/crisis-report";
import { notifyActivityChange } from "@/lib/activity-events";

export const Route = createFileRoute("/mood")({
  head: () => ({
    meta: [
      { name: "robots", content: "noindex" },
      { title: "Mood Check-in — Recovery Trauma™" },
      {
        name: "description",
        content: "A gentle space to notice how you're feeling — at your own pace.",
      },
      { property: "og:title", content: "Mood Check-in — Recovery Trauma™" },
      { property: "og:description", content: "A gentle space to notice how you're feeling — at your own pace." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:url", content: "https://recoverytrauma.com/mood" },
    ],
    links: [{ rel: "canonical", href: "https://recoverytrauma.com/mood" }],
  }),
  component: withMemberGate(MoodPage, "Mood Check"),
});

const moodConfig: Record<
  number,
  { label: string; emoji: string; color: string }
> = {
  1: { label: "Anxious", emoji: "😟", color: "oklch(0.65 0.1 245)" },
  2: { label: "Sad", emoji: "😔", color: "oklch(0.6 0.08 255)" },
  3: { label: "Frustrated", emoji: "😡", color: "oklch(0.62 0.14 35)" },
  4: { label: "Exhausted", emoji: "😴", color: "oklch(0.55 0.05 265)" },
  5: { label: "Numb", emoji: "😐", color: "oklch(0.6 0.03 265)" },
  6: { label: "Calm", emoji: "🌿", color: "oklch(0.72 0.12 155)" },
  7: { label: "Hopeful", emoji: "🙂", color: "oklch(0.78 0.1 95)" },
  8: { label: "Grateful", emoji: "💛", color: "oklch(0.82 0.14 85)" },
  9: { label: "Strong", emoji: "😊", color: "oklch(0.72 0.12 145)" },
  10: { label: "Thriving", emoji: "🌻", color: "oklch(0.85 0.16 90)" },
};

const levels = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

function MoodPage() {
  const [selectedLevel, setSelectedLevel] = useState<number | null>(null);
  const [notes, setNotes] = useState("");
  const queryClient = useQueryClient();

  const fetchMoods = useServerFn(getMoodChecks);
  const createCheck = useServerFn(createMoodCheck);

  const { data, isLoading } = useQuery({
    queryKey: ["mood-checks"],
    queryFn: fetchMoods,
  });

  const createMutation = useMutation({
    mutationFn: (vars: { mood_level: number; notes?: string }) =>
      createCheck({ data: vars }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["mood-checks"] });
      notifyActivityChange("mood");
      setSelectedLevel(null);
      setNotes("");
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedLevel == null) return;
    createMutation.mutate({
      mood_level: selectedLevel,
      notes: notes || undefined,
    });
    if (notes.trim()) {
      void reportCrisisIfDetected({ text: notes, source: "mood_check" });
    }
  };

  const checks = data?.checks ?? [];
  const recentChecks = [...checks].reverse().slice(0, 14);
  const avgMood =
    checks.length > 0
      ? (
          checks.reduce((sum, c) => sum + c.mood_level, 0) / checks.length
        ).toFixed(1)
      : null;

  return (
    <div className="mx-auto max-w-3xl px-4 py-10">
      <div className="mb-8">
        <h1 className="font-serif text-3xl text-foreground">
          🌱 Mood check-in
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Take a moment to notice how you're feeling right now. There are no
          right or wrong answers.
        </p>
      </div>

      {/* Why this matters */}
      <div className="mb-8 rounded-2xl border border-border/40 bg-muted/30 p-5">
        <div className="mb-2 flex items-center gap-2 text-sm font-medium text-foreground">
          <Sparkles className="h-4 w-4 text-primary" />
          Why this matters
        </div>
        <p className="text-sm leading-relaxed text-muted-foreground">
          Trauma can disconnect us from our emotions, body, and inner
          experiences. Regular mood check-ins help build self-awareness,
          identify patterns, recognize triggers, and celebrate progress in your
          recovery journey. Small moments of awareness can lead to meaningful
          change over time.
        </p>
      </div>

      {/* Check-in form */}
      <form
        onSubmit={handleSubmit}
        className="mb-10 rounded-2xl border border-border/50 bg-card p-6"
      >
        <label className="mb-4 block text-sm font-medium text-foreground">
          How are you feeling right now?
        </label>

        <div className="mb-4 grid grid-cols-5 gap-2 sm:grid-cols-10">
          {levels.map((level) => {
            const config = moodConfig[level];
            const isSelected = selectedLevel === level;
            return (
              <button
                key={level}
                type="button"
                onClick={() => setSelectedLevel(level)}
                title={`${level} — ${config.label}`}
                className={`flex flex-col items-center gap-1 rounded-xl border-2 px-1 py-3 transition-all ${
                  isSelected
                    ? "scale-110 border-primary bg-primary/10 shadow-lg"
                    : "border-border/40 bg-background hover:border-border hover:bg-muted/40"
                }`}
              >
                <span
                  className={`text-2xl transition-transform ${
                    isSelected ? "scale-110" : ""
                  }`}
                >
                  {config.emoji}
                </span>
                <span
                  className={`text-[11px] font-medium ${
                    isSelected ? "text-primary" : "text-muted-foreground"
                  }`}
                >
                  {level}
                </span>
              </button>
            );
          })}
        </div>

        {selectedLevel && (
          <p className="mb-4 text-center text-sm text-muted-foreground">
            <span className="me-1 text-base">
              {moodConfig[selectedLevel].emoji}
            </span>
            <span className="font-medium text-foreground">
              {moodConfig[selectedLevel].label}
            </span>
          </p>
        )}

        <div className="mb-4">
          <label className="mb-1 block text-sm font-medium text-foreground">
            Notes (optional)
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Anything on your mind?"
            rows={3}
            className="w-full resize-none rounded-lg border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </div>
        <button
          type="submit"
          disabled={selectedLevel == null || createMutation.isPending}
          onClick={(ev) => {
            // iOS Safari fallback: blur active input so the first tap
            // reliably submits even when the soft keyboard is open.
            if (selectedLevel == null || createMutation.isPending) return;
            ev.preventDefault();
            if (
              typeof document !== "undefined" &&
              document.activeElement instanceof HTMLElement
            ) {
              document.activeElement.blur();
            }
            handleSubmit(ev as unknown as React.FormEvent);
          }}
          className="relative z-20 inline-flex min-h-[48px] w-full touch-manipulation items-center justify-center rounded-lg bg-primary px-4 py-3 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-50 sm:min-h-0 sm:w-auto sm:py-2"
        >
          {createMutation.isPending ? "Saving..." : "Save check-in"}
        </button>

        <p className="mt-4 text-center text-xs italic text-muted-foreground">
          Every feeling is welcome here. Healing is not a straight line.
        </p>
      </form>

      {/* Mood history */}
      <div className="rounded-2xl border border-border/50 bg-card p-6">
        <div className="mb-6 flex items-center justify-between">
          <h2 className="font-serif text-xl">Mood history</h2>
          {avgMood && (
            <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
              <TrendingUp className="h-4 w-4" />
              Average:{" "}
              <span className="font-medium text-foreground">{avgMood}/10</span>
            </div>
          )}
        </div>

        {isLoading ? (
          <div className="h-40 animate-pulse rounded-xl bg-muted" />
        ) : recentChecks.length > 0 ? (
          <div className="space-y-3">
            {recentChecks.map((check) => {
              const config = moodConfig[check.mood_level] ?? moodConfig[5];
              return (
                <div
                  key={check.id}
                  className="flex items-center gap-4 rounded-xl border border-border/30 bg-surface p-4"
                >
                  <div
                    className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full text-2xl"
                    style={{ backgroundColor: config.color }}
                  >
                    {config.emoji}
                  </div>
                  <div className="flex-1">
                    <p className="text-xs text-muted-foreground">
                      {new Date(check.created_at).toLocaleDateString("en-US", {
                        weekday: "long",
                        month: "short",
                        day: "numeric",
                        hour: "numeric",
                        minute: "2-digit",
                      })}
                    </p>
                    {check.notes && (
                      <p className="mt-1 text-sm text-foreground">
                        {check.notes}
                      </p>
                    )}
                  </div>
                  <span className="text-sm font-medium text-muted-foreground">
                    {check.mood_level} · {config.label}
                  </span>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="py-8 text-center">
            <span className="text-4xl">🌱</span>
            <p className="mt-3 text-sm text-muted-foreground">
              No check-ins yet. Take your first one above.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
